import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BlockchainVerifier = ({ entry, isOpen, onClose, className = '' }) => {
  const [verificationStep, setVerificationStep] = useState(0);
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const verificationSteps = [
    {
      id: 'hash_check',
      label: 'Hash Verification',
      description: 'Verifying data integrity using cryptographic hash',
      icon: 'Hash'
    },
    {
      id: 'blockchain_lookup',
      label: 'Blockchain Lookup',
      description: 'Searching transaction on blockchain network',
      icon: 'Search'
    },
    {
      id: 'timestamp_validation',
      label: 'Timestamp Validation',
      description: 'Validating transaction timestamp authenticity',
      icon: 'Clock'
    },
    {
      id: 'signature_check',
      label: 'Digital Signature',
      description: 'Verifying digital signature authenticity',
      icon: 'Shield'
    },
    {
      id: 'consensus_check',
      label: 'Network Consensus',
      description: 'Checking network consensus validation',
      icon: 'CheckCircle'
    }
  ];

  useEffect(() => {
    if (isOpen && entry) {
      startVerification();
    }
  }, [isOpen, entry]);

  const startVerification = async () => {
    setIsVerifying(true);
    setVerificationStep(0);
    setVerificationResult(null);

    // Simulate verification process
    for (let i = 0; i < verificationSteps?.length; i++) {
      setVerificationStep(i);
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
    }

    // Simulate verification result
    const success = Math.random() > 0.1; // 90% success rate
    setVerificationResult({
      success,
      timestamp: new Date(),
      details: {
        hashMatch: success,
        blockchainConfirmed: success,
        timestampValid: success,
        signatureValid: success,
        consensusReached: success,
        networkLatency: Math.floor(Math.random() * 500) + 100,
        confirmations: success ? Math.floor(Math.random() * 50) + 10 : 0
      }
    });

    setIsVerifying(false);
  };

  const getStepStatus = (stepIndex) => {
    if (isVerifying) {
      if (stepIndex < verificationStep) return 'completed';
      if (stepIndex === verificationStep) return 'active';
      return 'pending';
    } else if (verificationResult) {
      return verificationResult?.success ? 'completed' : 'failed';
    }
    return 'pending';
  };

  const getStepIcon = (step, status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'active': return step?.icon;
      case 'failed': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'active': return 'text-primary';
      case 'failed': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  if (!isOpen || !entry) return null;

  return (
    <div className={`fixed inset-0 z-50 ${className}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-popover border border-border rounded-lg shadow-modal animate-slide-down">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <Icon name="Shield" size={24} className="text-primary" />
              <div>
                <h2 className="text-lg font-semibold text-popover-foreground">
                  Blockchain Verification
                </h2>
                <p className="text-sm text-muted-foreground">
                  Verifying data integrity and authenticity
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors duration-150"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Entry Information */}
          <div className="p-6 border-b border-border bg-muted/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Transaction Hash:</span>
                <p className="font-mono text-popover-foreground break-all">
                  {entry?.blockchainHash}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Block Number:</span>
                <p className="font-mono text-popover-foreground">
                  {entry?.blockNumber}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Action:</span>
                <p className="text-popover-foreground">{entry?.action}</p>
              </div>
              <div>
                <span className="text-muted-foreground">User:</span>
                <p className="text-popover-foreground">{entry?.user}</p>
              </div>
            </div>
          </div>

          {/* Verification Steps */}
          <div className="p-6">
            <h3 className="font-medium text-popover-foreground mb-4">
              Verification Process
            </h3>
            
            <div className="space-y-4">
              {verificationSteps?.map((step, index) => {
                const status = getStepStatus(index);
                const stepIcon = getStepIcon(step, status);
                const stepColor = getStepColor(status);
                
                return (
                  <div key={step?.id} className="flex items-center space-x-4">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                      status === 'completed' ? 'bg-success border-success' :
                      status === 'active' ? 'bg-primary border-primary' :
                      status === 'failed'? 'bg-error border-error' : 'border-muted'
                    }`}>
                      <Icon 
                        name={stepIcon} 
                        size={16} 
                        className={status === 'pending' ? 'text-muted-foreground' : 'text-white'}
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-medium ${stepColor}`}>
                        {step?.label}
                        {status === 'active' && isVerifying && (
                          <Icon name="Loader2" size={16} className="inline ml-2 animate-spin" />
                        )}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {step?.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Verification Result */}
          {verificationResult && (
            <div className="p-6 border-t border-border">
              <div className={`p-4 rounded-lg ${
                verificationResult?.success 
                  ? 'bg-success/5 border border-success/20' :'bg-error/5 border border-error/20'
              }`}>
                <div className="flex items-center space-x-3 mb-3">
                  <Icon 
                    name={verificationResult?.success ? "CheckCircle" : "XCircle"} 
                    size={20} 
                    className={verificationResult?.success ? "text-success" : "text-error"}
                  />
                  <h4 className={`font-medium ${
                    verificationResult?.success ? "text-success" : "text-error"
                  }`}>
                    {verificationResult?.success 
                      ? "Verification Successful" 
                      : "Verification Failed"
                    }
                  </h4>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Network Latency:</span>
                    <p className="font-mono">{verificationResult?.details?.networkLatency}ms</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Confirmations:</span>
                    <p className="font-mono">{verificationResult?.details?.confirmations}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Verified At:</span>
                    <p className="font-mono">
                      {verificationResult?.timestamp?.toLocaleString('en-GB', {
                        timeZone: 'Europe/Athens'
                      })}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <p className={`font-medium ${
                      verificationResult?.success ? "text-success" : "text-error"
                    }`}>
                      {verificationResult?.success ? "Authentic" : "Compromised"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between p-6 border-t border-border">
            <Button
              variant="outline"
              onClick={startVerification}
              disabled={isVerifying}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Re-verify
            </Button>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
              >
                Export Report
              </Button>
              <Button
                variant="default"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockchainVerifier;